import type { PortfolioSummary, PositionView } from "../portfolio/usePortfolio";
import { weightedAvg } from "./dashboardData";
import { Shimmer } from "../../ui/loaders";
import { fmtNum, fmtPct, fmtRub } from "../../lib/format";

// 4 KPI-карточки сверху дашборда. Расширенный набор по сравнению с
// портфельными карточками: тут вместо «нереализ. P&L» — средневзвешенная
// net YTM + средний срок (главные характеристики «качества» портфеля).
export function KpiStrip({
  summary,
  positions,
}: {
  summary: PortfolioSummary | undefined;
  positions: PositionView[] | undefined;
}) {
  const loading = summary == null || positions == null;
  const pl = summary?.total_unrealized_pnl_rub ?? 0;
  const plClass = !loading ? (pl > 0 ? "ok" : pl < 0 ? "bad" : "") : "";
  const plPct = summary?.total_unrealized_pnl_pct ?? null;

  const wYtm = positions ? weightedAvg(positions, (v) => v.entry_net_ytm) : null;
  const wDays = positions
    ? weightedAvg(positions, (v) =>
        v.days_to_maturity == null ? null : v.days_to_maturity,
      )
    : null;
  const wMonths = wDays == null ? null : wDays / 30.44;

  return (
    <div className="grid g-4" style={{ gap: 12, marginBottom: 14 }}>
      <div className="card" style={{ padding: 14 }}>
        <div className="label">всего вложено</div>
        <div className="big-num" style={{ fontSize: 26 }}>
          {loading ? (
            <Shimmer width={140} height={26} />
          ) : (
            fmtRub(summary!.total_cost_basis_rub)
          )}
        </div>
        <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>
          {loading ? (
            <Shimmer width={80} height={12} />
          ) : (
            <>{summary!.position_count} позиций</>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div className="label">текущая стоимость</div>
        <div className="big-num" style={{ fontSize: 26 }}>
          {loading ? (
            <Shimmer width={140} height={26} />
          ) : (
            fmtRub(summary!.total_market_value_rub)
          )}
        </div>
        <div className={plClass} style={{ fontSize: 12, marginTop: 4 }}>
          {loading ? (
            <Shimmer width={120} height={12} />
          ) : (
            <>
              {pl >= 0 ? "+" : ""}
              {fmtRub(pl)}
              {plPct != null && (
                <>
                  {" · "}
                  {plPct >= 0 ? "+" : ""}
                  {fmtPct(plPct)}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div
        className="card"
        style={{
          padding: 14,
          background:
            "linear-gradient(180deg, rgba(29,78,137,0.08), transparent)",
        }}
      >
        <div className="label">ожидается до horizon</div>
        <div className="big-num accent" style={{ fontSize: 26 }}>
          {loading ? (
            <Shimmer width={140} height={26} />
          ) : (
            <>+{fmtRub(summary!.total_expected_return_rub)}</>
          )}
        </div>
        <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>
          купоны + погашения, после налога
        </div>
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div className="label">средневзв. net ytm</div>
        <div className="big-num accent" style={{ fontSize: 26 }}>
          {loading ? (
            <Shimmer width={100} height={26} />
          ) : wYtm == null ? (
            "—"
          ) : (
            fmtPct(wYtm)
          )}
        </div>
        <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>
          {loading ? (
            <Shimmer width={120} height={12} />
          ) : wMonths == null ? (
            "средний срок —"
          ) : (
            `средний срок ${fmtNum(wMonths, 1)} мес`
          )}
        </div>
      </div>
    </div>
  );
}
